// pages/reports/AnalyticsPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, Stack, Grid, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Button, CircularProgress,
  alpha, Fade, Tooltip, ToggleButton, ToggleButtonGroup, Autocomplete, Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  BarChart as BarIcon,
  ShowChart as LineIcon,
  PieChart as PieIcon,
  ShoppingCart as PurchaseIcon,
  TrendingUp as SellIcon,
  AssignmentReturn as ReturnIcon,
  AccountBalance as CostIcon,
  Analytics as ProfitIcon,
  FilterList as FilterIcon,
  CalendarToday as CalIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { apiService } from '../../services/api';
import { tradesService, Product, ProductCategory, CostCategory } from '../../services/tradesService';

// ── Types ─────────────────────────────────────────────────────────────────────
interface PurchasePoint { period: string; product_name: string; product_id: number; total: number; }
interface SellPoint     { period: string; product_name: string; product_id: number; total: number; cash: number; due: number; }
interface ReturnPoint   { period: string; product_name: string; product_id: number; total: number; }
interface CostPoint     { period: string; category_name: string; category_id: number; total: number; }
interface ProfitPoint   { period: string; product_name: string; product_id: number; total_sales: number; total_purchase: number; total_profit: number; business_cost: number; commission: number; }

interface AnalyticsData {
  purchase:    PurchasePoint[];
  selling:     SellPoint[];
  return_:     ReturnPoint[];
  cost:        CostPoint[];
  total_profit: ProfitPoint[];
  summary: {
    total_purchase: number; total_sell: number; total_return: number;
    total_cost: number; total_profit: number; total_cash: number; total_due: number;
  };
}

// ── Colour palette ────────────────────────────────────────────────────────────
const CHART_COLORS = [
  '#667EEA','#4CAF50','#FF9800','#f44336','#9C27B0',
  '#00BCD4','#FF5722','#8BC34A','#E91E63','#3F51B5',
];

const SECTION_COLORS = {
  purchase:    '#2196F3',
  selling:     '#4CAF50',
  return_:     '#FF9800',
  cost:        '#9C27B0',
  total_profit: '#667EEA',
};

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt2 = (v: number) =>
  v?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00';

const fmtAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

// ── Custom tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={4} sx={{ p: 1.5, borderRadius: 2, minWidth: 160, border: '1px solid', borderColor: alpha('#667EEA', 0.2) }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5} color="text.secondary">
        {label}
      </Typography>
      {payload.map((p: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <Typography variant="caption" sx={{ color: p.color, fontWeight: 600 }}>{p.name}</Typography>
          <Typography variant="caption" fontWeight={700}>Tk. {fmt2(p.value)}</Typography>
        </Box>
      ))}
    </Paper>
  );
};

// ── Aggregate helpers ─────────────────────────────────────────────────────────
function aggregateByPeriod<T extends { period: string; total: number }>(
  data: T[]
): { period: string; total: number }[] {
  const map = new Map<string, number>();
  data.forEach(r => map.set(r.period, (map.get(r.period) || 0) + r.total));
  return Array.from(map.entries())
    .map(([period, total]) => ({ period, total }))
    .sort((a, b) => a.period.localeCompare(b.period));
}

function aggregateByProduct<T extends { product_name: string; total: number }>(
  data: T[]
): { product_name: string; total: number }[] {
  const map = new Map<string, number>();
  data.forEach(r => map.set(r.product_name, (map.get(r.product_name) || 0) + r.total));
  return Array.from(map.entries()).map(([product_name, total]) => ({ product_name, total }));
}

function aggregateByCategory(data: CostPoint[]): { category_name: string; total: number }[] {
  const map = new Map<string, number>();
  data.forEach(r => map.set(r.category_name, (map.get(r.category_name) || 0) + r.total));
  return Array.from(map.entries()).map(([category_name, total]) => ({ category_name, total }));
}

// ── Chart type toggle ─────────────────────────────────────────────────────────
type ChartType = 'bar' | 'line' | 'pie';
type ViewMode  = 'period' | 'product';

const ChartTypeToggle: React.FC<{ value: ChartType; onChange: (v: ChartType) => void; color: string }> = ({ value, onChange, color }) => (
  <ToggleButtonGroup value={value} exclusive size="small"
    onChange={(_, v) => v && onChange(v)}
    sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, border: `1px solid ${alpha(color, 0.3)}`, color: 'text.secondary',
      '&.Mui-selected': { background: alpha(color, 0.12), color, fontWeight: 700 } } }}>
    <ToggleButton value="bar"><BarIcon fontSize="small" /></ToggleButton>
    <ToggleButton value="line"><LineIcon fontSize="small" /></ToggleButton>
    <ToggleButton value="pie"><PieIcon fontSize="small" /></ToggleButton>
  </ToggleButtonGroup>
);

// ── Mini donut + legend ───────────────────────────────────────────────────────
const MiniDonut: React.FC<{
  data: { name: string; value: number }[];
  colors?: string[];
}> = ({ data, colors = CHART_COLORS }) => {
  const total = data.reduce((s, r) => s + r.value, 0);
  return (
    <Box>
      <Box sx={{ height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="name"
              cx="50%" cy="50%"
              innerRadius="50%" outerRadius="80%"
              paddingAngle={2} label={false} labelLine={false}
            >
              {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Pie>
            <RTooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Stack spacing={0.5} mt={1}>
        {data.map((item, i) => {
          const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0';
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, background: colors[i % colors.length] }} />
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, fontSize: '0.68rem' }}>
                {item.name}
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.68rem', flexShrink: 0 }}>
                {pct}%
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

// ── Universal chart renderer ──────────────────────────────────────────────────
const UniversalChart: React.FC<{
  type:       ChartType;
  data:       any[];
  dataKey:    string;
  nameKey:    string;
  color:      string;
  multiKeys?: { key: string; color: string; name: string }[];
}> = ({ type, data, dataKey, nameKey, color, multiKeys }) => {
  const h = 280;

  if (type === 'pie') {
    return (
      <Box>
        <Box sx={{ height: h }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data} dataKey={dataKey} nameKey={nameKey}
                cx="50%" cy="50%"
                innerRadius="30%" outerRadius="60%"
                paddingAngle={2} label={false} labelLine={false}
              >
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <RTooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', mt: 1 }}>
          {data.map((item: any, i: number) => {
            const total = data.reduce((s: number, r: any) => s + r[dataKey], 0);
            const pct   = total > 0 ? ((item[dataKey] / total) * 100).toFixed(1) : '0.0';
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem' }}>
                  {item[nameKey]}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.68rem', flexShrink: 0, ml: 0.5 }}>
                  {pct}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  if (type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={h}>
        <LineChart data={data} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.06)} />
          <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} />
          <RTooltip content={<CustomTooltip />} />
          <Legend />
          {multiKeys
            ? multiKeys.map(mk => <Line key={mk.key} type="monotone" dataKey={mk.key} stroke={mk.color} strokeWidth={2} dot={false} name={mk.name} />)
            : <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} name="Amount (Tk.)" />}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // bar (default)
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.06)} />
        <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} />
        <RTooltip content={<CustomTooltip />} />
        <Legend />
        {multiKeys
          ? multiKeys.map(mk => <Bar key={mk.key} dataKey={mk.key} fill={mk.color} radius={[4,4,0,0]} name={mk.name} />)
          : <Bar dataKey={dataKey} fill={color} radius={[4,4,0,0]} name="Amount (Tk.)" />}
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Section card wrapper ──────────────────────────────────────────────────────
const SectionCard: React.FC<{
  title: string; subtitle: string; color: string; icon: React.ReactNode;
  total: number; loading: boolean; children: React.ReactNode;
  topRight?: React.ReactNode;
}> = ({ title, subtitle, color, icon, total, loading, children, topRight }) => (
  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(color, 0.18)}`,
    overflow: 'hidden', boxShadow: `0 2px 12px ${alpha(color, 0.07)}` }}>
    <Box sx={{ px: 2.5, py: 2, background: alpha(color, 0.04), borderBottom: `1px solid ${alpha(color, 0.1)}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: alpha(color, 0.12),
          display: 'flex', alignItems: 'center', justifyContent: 'center', color, '& svg': { fontSize: 20 } }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {topRight}
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
          <Typography variant="h6" fontWeight={800} sx={{ color, lineHeight: 1 }}>
            Tk. {loading ? '—' : fmt2(total)}
          </Typography>
        </Box>
      </Box>
    </Box>
    <Box sx={{ p: 2 }}>
      {loading
        ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} sx={{ color }} /></Box>
        : children}
    </Box>
  </Card>
);

// ── Summary stat chip ─────────────────────────────────────────────────────────
const StatChip: React.FC<{ label: string; value: number; color: string; loading: boolean }> = ({ label, value, color, loading }) => (
  <Box sx={{ flex: 1, minWidth: 130, p: 1.75, borderRadius: 3,
    border: `1px solid ${alpha(color, 0.2)}`, background: alpha(color, 0.04) }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={800} sx={{ color, fontSize: '1rem' }}>
      {loading ? <CircularProgress size={14} sx={{ color }} /> : `Tk. ${fmt2(value)}`}
    </Typography>
  </Box>
);

// ── View mode toggle (reusable) ───────────────────────────────────────────────
const ViewToggle: React.FC<{ value: ViewMode; onChange: (v: ViewMode) => void; color: string }> = ({ value, onChange, color }) => (
  <ToggleButtonGroup value={value} exclusive size="small"
    onChange={(_, v) => v && onChange(v)}
    sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.4, fontSize: '0.7rem',
      border: `1px solid ${alpha(color, 0.3)}`,
      '&.Mui-selected': { background: alpha(color, 0.12), color } } }}>
    <ToggleButton value="period">By Date</ToggleButton>
    <ToggleButton value="product">By Product</ToggleButton>
  </ToggleButtonGroup>
);

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const AnalyticsPage: React.FC = () => {

  const [data, setData]       = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [groupBy,      setGroupBy]      = useState<'daily' | 'monthly'>('daily');
  const [selProduct,   setSelProduct]   = useState<Product | null>(null);
  const [selCategory,  setSelCategory]  = useState<ProductCategory | null>(null);

  // Ref data
  const [products,   setProducts]   = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  // Per-section chart type + view mode
  const [chartTypes, setChartTypes] = useState<Record<string, ChartType>>({
    purchase: 'bar', selling: 'bar', return_: 'bar', cost: 'pie', total_profit: 'bar',
  });
  const [viewModes, setViewModes] = useState<Record<string, ViewMode>>({
    purchase: 'period', selling: 'period', return_: 'period', total_profit: 'period',
  });

  // ── Load dropdown data ─────────────────────────────────────────────────────
  useEffect(() => {
    tradesService.listProducts({ is_active: true })
      .then(r => setProducts(r.results)).catch(() => {});
    tradesService.listCategories({ is_active: true })
      .then(r => setCategories(r.results)).catch(() => {});
  }, []);

  // ── Fetch analytics ────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { group_by: groupBy };
      if (dateFrom)    params.date_from   = dateFrom;
      if (dateTo)      params.date_to     = dateTo;
      if (selProduct)  params.product_id  = String(selProduct.product_id);
      if (selCategory) params.category_id = String(selCategory.category_id);
      const res = await apiService.get<{ success: boolean } & AnalyticsData>('/trades/analytics/', { params });
      setData(res);
    } catch { } finally { setLoading(false); }
  }, [dateFrom, dateTo, groupBy, selProduct, selCategory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => {
    setDateFrom(''); setDateTo('');
    setSelProduct(null); setSelCategory(null);
    setGroupBy('daily');
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  const purchasePeriod  = useMemo(() => aggregateByPeriod(data?.purchase  || []), [data]);
  const purchaseProduct = useMemo(() => aggregateByProduct(data?.purchase || []), [data]);
  const sellPeriod      = useMemo(() => aggregateByPeriod(data?.selling   || []), [data]);
  const sellProduct     = useMemo(() => aggregateByProduct(data?.selling  || []), [data]);
  const returnPeriod    = useMemo(() => aggregateByPeriod(data?.return_   || []), [data]);
  const returnProduct   = useMemo(() => aggregateByProduct(data?.return_  || []), [data]);
  const costPeriod      = useMemo(() => aggregateByPeriod(data?.cost      || []), [data]);
  const costCategory    = useMemo(() => aggregateByCategory(data?.cost    || []), [data]);

  const sellByPeriod = useMemo(() => {
    const map = new Map<string, any>();
    (data?.selling || []).forEach(r => {
      const ex = map.get(r.period) || { period: r.period, total: 0, cash: 0, due: 0 };
      ex.total += r.total; ex.cash += r.cash; ex.due += r.due;
      map.set(r.period, ex);
    });
    return Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
  }, [data]);

  const plPeriod = useMemo(() => {
    const map = new Map<string, { period: string; total_sales: number; total_purchase: number; total_profit: number }>();
    (data?.total_profit || []).forEach(r => {
      const ex = map.get(r.period) || { period: r.period, total_sales: 0, total_purchase: 0, total_profit: 0 };
      ex.total_sales    += r.total_sales;
      ex.total_purchase += r.total_purchase;
      ex.total_profit    += r.total_profit;
      map.set(r.period, ex);
    });
    return Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
  }, [data]);

  const plProduct = useMemo(() => {
    const map = new Map<string, { product_name: string; total_profit: number }>();
    (data?.total_profit || []).forEach(r => {
      const ex = map.get(r.product_name) || { product_name: r.product_name, total_profit: 0 };
      ex.total_profit += r.total_profit;
      map.set(r.product_name, ex);
    });
    return Array.from(map.values());
  }, [data]);

  const setChart = (s: string, v: ChartType)  => setChartTypes(p => ({ ...p, [s]: v }));
  const setView  = (s: string, v: ViewMode)   => setViewModes(p  => ({ ...p, [s]: v }));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Fade in timeout={400}>
      <Box>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{
              background: 'linear-gradient(135deg, #667EEA 0%, #764ba2 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Visual insights across purchase, sales, returns, costs and profit/loss
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
              onClick={fetchData} disabled={loading}
              sx={{ borderRadius: 2, borderColor: alpha('#667EEA', 0.4), color: '#667EEA' }}>
              Refresh
            </Button>
          </Tooltip>
        </Box>

        {/* ── Filters ──────────────────────────────────────────────────── */}
        <Card elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: `1px solid ${alpha('#667EEA', 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterIcon sx={{ fontSize: 16, color: '#667EEA' }} />
            <Typography variant="body2" fontWeight={700} color="#667EEA">Filters</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
            <TextField size="small" label="From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <TextField size="small" label="To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <Autocomplete size="small" options={products}
              getOptionLabel={o => o.product_name} value={selProduct}
              onChange={(_, v) => setSelProduct(v)} sx={{ minWidth: 220 }}
              renderInput={params => (
                <TextField {...params} label="Product" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Autocomplete size="small" options={categories}
              getOptionLabel={o => o.category_name} value={selCategory}
              onChange={(_, v) => setSelCategory(v)} sx={{ minWidth: 180 }}
              renderInput={params => (
                <TextField {...params} label="Category" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <FormControl size="small" sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Group By</InputLabel>
              <Select value={groupBy} label="Group By"
                onChange={e => setGroupBy(e.target.value as 'daily' | 'monthly')}>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>

            <Button size="small" variant="outlined" onClick={clearFilters}
              sx={{ borderRadius: 2, borderColor: alpha('#667EEA', 0.4), color: '#667EEA' }}>
              Clear All
            </Button>
          </Stack>
        </Card>

        {/* ── Summary strip ────────────────────────────────────────────── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
          <StatChip label="Total Purchase"  value={data?.summary.total_purchase    ?? 0} color="#2196F3" loading={loading} />
          <StatChip label="Total Sales"     value={data?.summary.total_sell        ?? 0} color="#4CAF50" loading={loading} />
          <StatChip label="Total Cash"      value={data?.summary.total_cash        ?? 0} color="#00BCD4" loading={loading} />
          <StatChip label="Total Due"       value={data?.summary.total_due         ?? 0} color="#f44336" loading={loading} />
          <StatChip label="Total Returns"   value={data?.summary.total_return      ?? 0} color="#FF9800" loading={loading} />
          <StatChip label="Total Cost"      value={data?.summary.total_cost        ?? 0} color="#9C27B0" loading={loading} />
          <StatChip label="Profit/Loss" value={data?.summary.total_profit ?? 0}
            color={(data?.summary.total_profit ?? 0) >= 0 ? '#4CAF50' : '#f44336'} loading={loading} />
        </Stack>

        {/* ── Sections ─────────────────────────────────────────────────── */}
        <Stack spacing={3}>

          {/* ── 1. PURCHASE ─────────────────────────────────────────── */}
          <SectionCard
            title="Purchase Analysis" subtitle="Total purchase amount by product over time"
            color={SECTION_COLORS.purchase} icon={<PurchaseIcon />}
            loading={loading} total={data?.summary.total_purchase ?? 0}
            topRight={
              <Stack direction="row" spacing={1} alignItems="center">
                <ViewToggle value={viewModes.purchase} onChange={v => setView('purchase', v)} color={SECTION_COLORS.purchase} />
                <ChartTypeToggle value={chartTypes.purchase} onChange={v => setChart('purchase', v)} color={SECTION_COLORS.purchase} />
              </Stack>
            }>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
                  {viewModes.purchase === 'period' ? 'Amount by Date' : 'Amount by Product'}
                </Typography>
                <UniversalChart
                  type={chartTypes.purchase}
                  data={viewModes.purchase === 'period' ? purchasePeriod : purchaseProduct}
                  dataKey="total"
                  nameKey={viewModes.purchase === 'period' ? 'period' : 'product_name'}
                  color={SECTION_COLORS.purchase}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">Top Products</Typography>
                <MiniDonut
                  data={purchaseProduct.slice(0, 6).map(r => ({ name: r.product_name, value: r.total }))}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* ── 2. SELLING ──────────────────────────────────────────── */}
          <SectionCard
            title="Selling Analysis" subtitle="Sales amount with cash and due breakdown"
            color={SECTION_COLORS.selling} icon={<SellIcon />}
            loading={loading} total={data?.summary.total_sell ?? 0}
            topRight={
              <Stack direction="row" spacing={1} alignItems="center">
                <ViewToggle value={viewModes.selling} onChange={v => setView('selling', v)} color={SECTION_COLORS.selling} />
                <ChartTypeToggle value={chartTypes.selling} onChange={v => setChart('selling', v)} color={SECTION_COLORS.selling} />
              </Stack>
            }>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
                  {viewModes.selling === 'period' ? 'Sales, Cash & Due by Date' : 'Amount by Product'}
                </Typography>
                {viewModes.selling === 'period' ? (
                  <UniversalChart
                    type={chartTypes.selling === 'pie' ? 'bar' : chartTypes.selling}
                    data={sellByPeriod}
                    dataKey="total" nameKey="period"
                    color={SECTION_COLORS.selling}
                    multiKeys={[
                      { key: 'total', color: SECTION_COLORS.selling, name: 'Total Sales' },
                      { key: 'cash',  color: '#00BCD4',              name: 'Cash' },
                      { key: 'due',   color: '#f44336',              name: 'Due' },
                    ]}
                  />
                ) : (
                  <UniversalChart type={chartTypes.selling} data={sellProduct}
                    dataKey="total" nameKey="product_name" color={SECTION_COLORS.selling} />
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">Cash vs Due split</Typography>
                <MiniDonut
                  data={[
                    { name: 'Cash', value: data?.summary.total_cash ?? 0 },
                    { name: 'Due',  value: data?.summary.total_due  ?? 0 },
                  ]}
                  colors={['#00BCD4', '#f44336']}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* ── 3. RETURN ───────────────────────────────────────────── */}
          <SectionCard
            title="Return Analysis" subtitle="Return amounts by product and date"
            color={SECTION_COLORS.return_} icon={<ReturnIcon />}
            loading={loading} total={data?.summary.total_return ?? 0}
            topRight={
              <Stack direction="row" spacing={1} alignItems="center">
                <ViewToggle value={viewModes.return_} onChange={v => setView('return_', v)} color={SECTION_COLORS.return_} />
                <ChartTypeToggle value={chartTypes.return_} onChange={v => setChart('return_', v)} color={SECTION_COLORS.return_} />
              </Stack>
            }>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
                  {viewModes.return_ === 'period' ? 'Amount by Date' : 'Amount by Product'}
                </Typography>
                <UniversalChart
                  type={chartTypes.return_}
                  data={viewModes.return_ === 'period' ? returnPeriod : returnProduct}
                  dataKey="total"
                  nameKey={viewModes.return_ === 'period' ? 'period' : 'product_name'}
                  color={SECTION_COLORS.return_}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">By Product</Typography>
                <MiniDonut
                  data={returnProduct.slice(0, 6).map(r => ({ name: r.product_name, value: r.total }))}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* ── 4. COST ─────────────────────────────────────────────── */}
          <SectionCard
            title="Cost Analysis" subtitle="Expenses breakdown by category and date"
            color={SECTION_COLORS.cost} icon={<CostIcon />}
            loading={loading} total={data?.summary.total_cost ?? 0}
            topRight={
              <ChartTypeToggle value={chartTypes.cost} onChange={v => setChart('cost', v)} color={SECTION_COLORS.cost} />
            }>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">Cost by Date</Typography>
                <UniversalChart
                  type={chartTypes.cost === 'pie' ? 'bar' : chartTypes.cost}
                  data={costPeriod} dataKey="total" nameKey="period" color={SECTION_COLORS.cost}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">By Category</Typography>
                <MiniDonut
                  data={costCategory.map(r => ({ name: r.category_name, value: r.total }))}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* ── 5. PROFIT / LOSS ────────────────────────────────────── */}
          <SectionCard
            title="Profit / Loss Analysis" subtitle="Net profit/loss after business cost and commission"
            color={SECTION_COLORS.total_profit} icon={<ProfitIcon />}
            loading={loading} total={data?.summary.total_profit ?? 0}
            topRight={
              <Stack direction="row" spacing={1} alignItems="center">
                <ViewToggle value={viewModes.total_profit} onChange={v => setView('total_profit', v)} color={SECTION_COLORS.total_profit} />
                <ChartTypeToggle value={chartTypes.total_profit} onChange={v => setChart('total_profit', v)} color={SECTION_COLORS.total_profit} />
              </Stack>
            }>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
                  {viewModes.total_profit === 'period' ? 'Sales vs Purchase vs Profit/Loss over Time' : 'Profit/Loss by Product'}
                </Typography>
                {viewModes.total_profit === 'period' ? (
                  <UniversalChart
                    type={chartTypes.total_profit === 'pie' ? 'bar' : chartTypes.total_profit}
                    data={plPeriod} dataKey="total_profit" nameKey="period"
                    color={SECTION_COLORS.total_profit}
                    multiKeys={[
                      { key: 'total_sales',    color: '#4CAF50', name: 'Total Sales' },
                      { key: 'total_purchase', color: '#2196F3', name: 'Total Purchase' },
                      { key: 'total_profit',    color: '#667EEA', name: 'Profit/Loss' },
                    ]}
                  />
                ) : (
                  <UniversalChart type={chartTypes.total_profit} data={plProduct}
                    dataKey="total_profit" nameKey="product_name" color={SECTION_COLORS.total_profit} />
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">P&L by Product</Typography>
                <MiniDonut
                  data={plProduct.filter(r => r.total_profit > 0).slice(0, 6).map(r => ({ name: r.product_name, value: r.total_profit }))}
                />
              </Grid>
            </Grid>

            {/* P&L breakdown table */}
            {(data?.total_profit?.length ?? 0) > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha('#667EEA', 0.1)}` }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" mb={1} display="block">
                  Product Breakdown
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: alpha('#667EEA', 0.06) }}>
                        {['Product', 'Sales', 'Purchase Cost', 'Business Cost 20%', 'Commission 40%', 'Profit/Loss'].map(h => (
                          <th key={h} style={{
                            padding: '8px 12px', fontWeight: 700, color: '#667EEA',
                            borderBottom: `2px solid ${alpha('#667EEA', 0.12)}`,
                            whiteSpace: 'nowrap',
                            textAlign: h === 'Product' ? 'left' : 'right' as any,
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {plProduct.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${alpha('#000', 0.05)}` }}>
                          <td style={{ padding: '7px 12px', fontWeight: 600 }}>{r.product_name}</td>
                          {[
                            { v: (data?.total_profit || []).filter(x => x.product_name === r.product_name).reduce((a, x) => a + x.total_sales,    0), c: '#4CAF50' },
                            { v: (data?.total_profit || []).filter(x => x.product_name === r.product_name).reduce((a, x) => a + x.total_purchase, 0), c: '#2196F3' },
                            { v: (data?.total_profit || []).filter(x => x.product_name === r.product_name).reduce((a, x) => a + x.business_cost,  0), c: '#FF9800' },
                            { v: (data?.total_profit || []).filter(x => x.product_name === r.product_name).reduce((a, x) => a + x.commission,     0), c: '#9C27B0' },
                            { v: r.total_profit, c: r.total_profit >= 0 ? '#4CAF50' : '#f44336' },
                          ].map((cell, ci) => (
                            <td key={ci} style={{ padding: '7px 12px', textAlign: 'right',
                              fontWeight: ci === 4 ? 800 : 400, color: cell.c }}>
                              Tk. {fmt2(cell.v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
          </SectionCard>

        </Stack>
      </Box>
    </Fade>
  );
};

export default AnalyticsPage;